// src/blogs/blogData.js
// Placeholder content for the three blog scenes, which map onto how finished
// the writing is: thoughts = raw, audio = refined ("longer"), movies =
// polished ("content"). No CMS/backend exists yet,
// so this is hand-authored mock data, structured the same way real content
// would be — swap these arrays for fetched/authored posts later without
// touching any component.
//
// Shared post shape: { id, title, date (ISO), tags, excerpt, body?, extra, theme }.
//
// Content is `excerpt` (one text paragraph) or, to mix in images, `body`: an
// array of blocks rendered in order by PostBody.jsx (`body` wins if both are
// set). Text-only posts can ignore `body` entirely.
//   body: [
//     "A paragraph. Newline characters in it become line breaks.",
//     { image: "blog/posts/rain.jpg",       // file under public/, or an https
//                                           // URL, or an imported asset
//       alt: "Rain over the harbor",        // describe it (falls back to caption)
//       caption: "Optional caption",
//       width: 1600, height: 900 },         // optional: the file's pixel size,
//                                           // stops the page jumping as it loads
//     "More text.",
//   ]
//
// Links: inside any text, [label](target) makes a link —
//   [my site](https://example.com)   external, opens in a new tab
//   [my email](mailto:me@example.com)
//   [that other post](post:movie-001)   another post by id, in any blog: opens
//                                       the right blog and scrolls to it
// Anything else (an unknown post id, javascript:, ...) stays plain text.
//
// Permalinks: every post can be opened directly at its blog's route plus
// #<id> — e.g. /resume/blog/thoughts#story-001 scrolls to that post. Each post
// has a small link icon in its top row that puts this URL in the address bar.
// So post ids must be unique across all three blogs.
//
// Arrays are sorted newest-first, which is the fixed order infinite scroll,
// the sidebar's date list, and edge-click prev/next all walk.
//
// `theme` is optional and per-post — when a post scrolls into view, BlogShell
// applies it over that blog's default look (see src/blogs/PostThemeBackdrop.jsx
// for how it's consumed). Every field is optional; omit any you don't want to
// override, and omit `theme` entirely for posts that should just use the
// blog's normal theme. Shape:
//   theme: {
//     bg?: string,              // solid color, feeds --blog-bg
//     text?: string,            // feeds --blog-text
//     accent?: string,          // feeds --blog-accent (frame/link/tag color)
//     accentContrast?: string,  // feeds --blog-accent-contrast
//     surface?: string,         // feeds --blog-surface (sidebar panel fill)
//     border?: string,          // feeds --blog-border
//     hideText?: boolean,       // the scene prints this post's text itself:
//                               // fade the post's own text out while it's the
//                               // active post (it stays in the document)
//     bgImage?: string,         // 2D backdrop image url — crossfades in;
//                               // ignored if `scene` is also set
//     scene?: keyof typeof import("./postScenes/index.js").POST_SCENES,
//                               // 3D backdrop — an R3F scene registered in
//                               // postScenes/index.js, takes priority over
//                               // bgImage when both are set
//   }

export const audioPosts = [
  { id: "audio-001", title: "Late Night Modular Session", date: "2026-08-18", tags: ["synth", "live-set"], excerpt: "Four hours, one patch, no plan — recorded straight off the desk with the mistakes left in.", extra: { durationLabel: "6:14" } },
  { id: "audio-002", title: "Field Recording: Rainstorm Over the Harbor", date: "2026-08-10", tags: ["field-recording", "ambient"], excerpt: "Fifteen minutes on the seawall as the first real storm of the season rolled in.", extra: { durationLabel: "4:02" } },
  { id: "audio-003", title: "Building a Granular Synth Patch", date: "2026-08-02", tags: ["synth", "sound-design"], excerpt: "Chopping a single vowel sound into a thousand tiny grains and stretching them across ten octaves.", extra: { durationLabel: "3:47" } },
];

export const moviePosts = [
  {
    id: "movie-004",
    title: "Holo Expirement",
    date: "2026-09-26",
    tags: ["experiment"],
    excerpt: "This post lives on a sheet of holographic foil. The words you're reading are printed on the cloth itself, so they bend, catch the light and shimmer as the fabric drifts.\n\nMove your cursor across it and the sheet ripples. If a post runs long, the text travels along the surface as you scroll, like a length of foil unrolling past a window.\n\nWhen the next post takes over, the cloth drifts away and the page goes back to its usual look.",
    extra: { posterSeed: 4, rating: 5.0, runtimeLabel: "1 min read" },
    // Holographic cloth backdrop with this post's text printed on it (see
    // postScenes/HoloClothScene.jsx). hideText fades the post's own DOM copy
    // out while the cloth is up; the colors restyle the rest of the page (the
    // sidebar etc.) for a dark ground while it's the active post.
    theme: {
      scene: "holoCloth",
      hideText: true,
      bg: "#0b0c12",
      text: "#ecebf5",
      surface: "rgba(16, 18, 26, 0.82)",
      border: "rgba(236, 235, 245, 0.16)",
      accent: "#a9b8ff",
      accentContrast: "#0b0c12",
    },
  },
  {
    id: "movie-001",
    title: "Rewatching Blade Runner in 4K",
    date: "2026-08-18",
    tags: ["sci-fi"],
    excerpt: "The rain reads differently at this resolution — you can see exactly how much of it is smoke.",
    extra: { posterSeed: 1, rating: 4.5, runtimeLabel: "1h 57m" },
    // Demo only, proving the per-post theme mechanism is wired end-to-end —
    // not a real design. Replace with an actual theme (or remove) once real
    // per-post looks are designed; see the shape note above.
    theme: {
      bg: "#12210f",
      accent: "#d4af37",
      accentContrast: "#12210f",
      scene: "placeholder",
    },
  },
  { id: "movie-002", title: "The Quiet Menace of Noir Lighting", date: "2026-08-10", tags: ["noir"], excerpt: "A short survey of how three films use a single practical lamp to do all their tension.", extra: { posterSeed: 2, rating: 4.0, runtimeLabel: "1h 45m" } },
  { id: "movie-003", title: "Why Spirited Away Still Holds Up", date: "2026-08-02", tags: ["animation"], excerpt: "Twenty-plus years on, and the bathhouse still feels more real than most live-action sets.", extra: { posterSeed: 3, rating: 5.0, runtimeLabel: "2h 05m" } },
];

export const thoughtsPosts = [
  { id: "story-001", title: "MaiGC with a cost", date: "2026-09-25", tags: ["narrative", "steal me"], excerpt: `I was thinking of a story idea/a story challenge. There is a world where they have an 'all knowing entity' (an AI on caliber with current LLM's), and whenever it hallucinates or says something contradictory to what is known to be true, reality itself shifts to make that statement true. I'll add more to this as it comes to me, but I think this is a good launching off point for atleast a short story. \n \n I think I was thinking about this because I've always thought of coders like wizards sitting in their isolated towers developing magic, and then when they shared their code on stack overflow or the likes, it was like communicating via their orbs. And I sort of see using AI coding companions as communing with some kind of ancestral entity to call forth code this kind of magic, but it has drawbacks.`, extra: { readMinutes: 5 } },
];

